import { Header } from '../components/Header';
import { Footer } from '../components/Footer';

export function Privacy() {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      <div className="pt-20 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl font-bold text-slate-900 mb-6">Privacy Policy</h1>
          <p className="text-slate-600 mb-8">Last updated: January 12, 2026</p>

          <div className="prose prose-lg max-w-none">
            <p className="text-slate-700 leading-relaxed mb-6">
              At dakkio.io, operated by MalexSoftware / RemoteSystems.ro ("we", "us", or
              "our"), we take your privacy seriously. This Privacy Policy explains how we
              collect, use, disclose, and safeguard your information when you use our website
              and service.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">
              1. Information We Collect
            </h2>

            <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-3">
              1.1 Personal Information
            </h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              We collect information that you voluntarily provide to us when you:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-6 text-slate-700">
              <li>Register for an account (name, email address)</li>
              <li>Subscribe to our newsletter</li>
              <li>Contact us for support or inquiries</li>
              <li>Participate in surveys or promotions</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-3">
              1.2 Usage Data
            </h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              We automatically collect certain information when you use our Service:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-6 text-slate-700">
              <li>IP address and device information</li>
              <li>Browser type and version</li>
              <li>Pages visited and time spent on pages</li>
              <li>Referring website addresses</li>
              <li>API usage statistics and patterns</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-3">
              1.3 Time-Series Data
            </h3>
            <p className="text-slate-700 leading-relaxed mb-6">
              We store the time-series data you send to our Service through our API. This
              data belongs to you and is processed solely for the purpose of providing
              monitoring and analytics services to you.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-3">
              1.4 Cookies and Tracking Technologies
            </h3>
            <p className="text-slate-700 leading-relaxed mb-6">
              We use cookies and similar tracking technologies to track activity on our
              Service and hold certain information. You can instruct your browser to refuse
              all cookies or to indicate when a cookie is being sent. See our Cookie Policy
              for more details.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">
              2. How We Use Your Information
            </h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              We use the collected information for various purposes:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-6 text-slate-700">
              <li>To provide and maintain our Service</li>
              <li>To notify you about changes to our Service</li>
              <li>To provide customer support</li>
              <li>To gather analysis or valuable information to improve our Service</li>
              <li>To monitor the usage of our Service</li>
              <li>To detect, prevent, and address technical issues</li>
              <li>To send you marketing communications (with your consent)</li>
              <li>To enforce our Terms of Service</li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">
              3. Legal Basis for Processing (GDPR)
            </h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              If you are from the European Economic Area (EEA), our legal basis for
              collecting and using your personal information depends on the data and the
              context:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-6 text-slate-700">
              <li>You have given us permission to do so</li>
              <li>Processing is in our legitimate interests</li>
              <li>To comply with the law</li>
              <li>To fulfill a contract with you</li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">
              4. Data Retention
            </h2>
            <p className="text-slate-700 leading-relaxed mb-6">
              We will retain your personal information only for as long as necessary for the
              purposes set out in this Privacy Policy. We will retain and use your information
              to the extent necessary to comply with our legal obligations, resolve disputes,
              and enforce our agreements.
            </p>
            <p className="text-slate-700 leading-relaxed mb-6">
              Time-series data is retained according to your subscription plan's retention
              policy (7 days to 10 years). You can delete your data at any time through your
              account dashboard.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">
              5. Data Security
            </h2>
            <p className="text-slate-700 leading-relaxed mb-6">
              The security of your data is important to us. We implement industry-standard
              security measures including:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-6 text-slate-700">
              <li>Encryption of data in transit (TLS/SSL)</li>
              <li>Encryption of data at rest</li>
              <li>Regular security audits and vulnerability assessments</li>
              <li>Access controls and authentication</li>
              <li>Multi-tenant data isolation</li>
              <li>Regular backups</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mb-6">
              However, no method of transmission over the Internet or electronic storage is
              100% secure. While we strive to use commercially acceptable means to protect
              your personal information, we cannot guarantee its absolute security.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">
              6. Data Sharing and Disclosure
            </h2>

            <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-3">
              6.1 Service Providers
            </h3>
            <p className="text-slate-700 leading-relaxed mb-6">
              We may employ third-party companies and individuals to facilitate our Service
              ("Service Providers"), provide the Service on our behalf, perform Service-related
              services, or assist us in analyzing how our Service is used. These third parties
              have access to your personal information only to perform these tasks on our
              behalf and are obligated not to disclose or use it for any other purpose.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-3">
              6.2 Legal Requirements
            </h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              We may disclose your personal information if required to do so by law or in
              response to valid requests by public authorities:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-6 text-slate-700">
              <li>To comply with a legal obligation</li>
              <li>To protect and defend our rights or property</li>
              <li>To prevent or investigate possible wrongdoing in connection with the Service</li>
              <li>To protect the personal safety of users of the Service or the public</li>
              <li>To protect against legal liability</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-3">
              6.3 Business Transfers
            </h3>
            <p className="text-slate-700 leading-relaxed mb-6">
              If we are involved in a merger, acquisition, or asset sale, your personal
              information may be transferred. We will provide notice before your personal
              information is transferred and becomes subject to a different Privacy Policy.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">
              7. Your Data Protection Rights (GDPR)
            </h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              If you are a resident of the European Economic Area (EEA), you have certain
              data protection rights:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-6 text-slate-700">
              <li>
                <strong>Right to Access:</strong> You can request copies of your personal data
              </li>
              <li>
                <strong>Right to Rectification:</strong> You can request correction of
                inaccurate data
              </li>
              <li>
                <strong>Right to Erasure:</strong> You can request deletion of your data
              </li>
              <li>
                <strong>Right to Restrict Processing:</strong> You can request restriction of
                processing
              </li>
              <li>
                <strong>Right to Data Portability:</strong> You can request transfer of your
                data
              </li>
              <li>
                <strong>Right to Object:</strong> You can object to processing of your data
              </li>
            </ul>
            <p className="text-slate-700 leading-relaxed mb-6">
              To exercise these rights, please contact us at privacy@dakkio.io. We will
              respond to your request within 30 days.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">
              8. California Privacy Rights (CCPA)
            </h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              If you are a California resident, you have specific rights regarding your
              personal information:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-6 text-slate-700">
              <li>Right to know what personal information is collected</li>
              <li>Right to know if personal information is sold or disclosed</li>
              <li>Right to say no to the sale of personal information</li>
              <li>Right to access your personal information</li>
              <li>Right to equal service and price</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mb-6">
              We do not sell your personal information. To exercise your rights under CCPA,
              contact us at privacy@dakkio.io.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">
              9. Children's Privacy
            </h2>
            <p className="text-slate-700 leading-relaxed mb-6">
              Our Service is not intended for anyone under the age of 18 ("Children"). We do
              not knowingly collect personally identifiable information from anyone under 18.
              If you are a parent or guardian and you are aware that your child has provided
              us with personal information, please contact us.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">
              10. International Data Transfers
            </h2>
            <p className="text-slate-700 leading-relaxed mb-6">
              Your information, including personal data, may be transferred to and maintained
              on computers located outside of your state, province, country, or other
              governmental jurisdiction where the data protection laws may differ. We will
              take all steps reasonably necessary to ensure that your data is treated securely
              and in accordance with this Privacy Policy.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">
              11. Third-Party Links
            </h2>
            <p className="text-slate-700 leading-relaxed mb-6">
              Our Service may contain links to other websites that are not operated by us.
              We have no control over and assume no responsibility for the content, privacy
              policies, or practices of any third-party sites or services.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">
              12. Changes to This Privacy Policy
            </h2>
            <p className="text-slate-700 leading-relaxed mb-6">
              We may update our Privacy Policy from time to time. We will notify you of any
              changes by posting the new Privacy Policy on this page and updating the "Last
              updated" date. You are advised to review this Privacy Policy periodically for
              any changes. Changes are effective when they are posted on this page.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">
              13. Data Processing Agreement
            </h2>
            <p className="text-slate-700 leading-relaxed mb-6">
              For Enterprise customers requiring a Data Processing Agreement (DPA) under GDPR
              or other data protection regulations, please contact us at legal@dakkio.io.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">
              14. Contact Us
            </h2>
            <p className="text-slate-700 leading-relaxed mb-6">
              If you have any questions about this Privacy Policy, please contact us:
            </p>
            <ul className="list-none space-y-2 mb-6 text-slate-700">
              <li>
                <strong>Email:</strong>{' '}
                <a
                  href="mailto:privacy@dakkio.io"
                  className="text-primary-600 hover:text-primary-700"
                >
                  privacy@dakkio.io
                </a>
              </li>
              <li>
                <strong>Data Protection Officer:</strong>{' '}
                <a
                  href="mailto:dpo@dakkio.io"
                  className="text-primary-600 hover:text-primary-700"
                >
                  dpo@dakkio.io
                </a>
              </li>
              <li>
                <strong>Website:</strong>{' '}
                <a
                  href="https://remotesystems.ro"
                  className="text-primary-600 hover:text-primary-700"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  remotesystems.ro
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
